import { Box, Chip, Paper, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import DiscountChip from '../../DiscountChip';
import { STATUS_SALE } from '../../../helpers/enums';
import { ccyFormat } from '../../../helpers/helper';
import { colorEfectivo, colorMixto, colotTransferencia } from '../../../styles';

const SalesAnalysisList = ({ salesData = [], groupByDate = true, viewDetail = false }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const getPaymentAmount = (sale, name) => {
    const payment = sale?.payments?.find((p) => p?.name?.toLowerCase() === name);
    return Number(payment?.amount || 0);
  };

  const getPaymentColor = (sale) => {
    const active = (sale?.payments || []).filter(p => Number(p?.amount || 0) > 0);
    if (active.length === 0) return '#d9d9d9';
    if (active.length > 1) return colorMixto;
    const name = active[0]?.name?.toLowerCase();
    if (name === 'efectivo') return colorEfectivo;
    if (name === 'transferencia') return colotTransferencia;
    return '#607d8b';
  };

  const getPaymentLabel = (sale) => {
    const active = (sale?.payments || []).filter(p => Number(p?.amount || 0) > 0);
    if (active.length === 0) return 'Sin pago';
    if (active.length > 1) return 'Mixto';
    const name = active[0]?.name || '';
    return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
  };

  const getPendingBySale = (sale) => {
    const saleTotal = Number(sale?.prices_sold?.sale_price || 0) * Number(sale?.quantity_sold || 0);
    return Math.max(saleTotal - Number(sale?.deposit || 0), 0);
  };

  const renderAmountsMeta = (sale) => {
    const deposit = Number(sale?.deposit || 0);
    const isReserved = sale?.status?.toLowerCase() === STATUS_SALE.RESERVED;
    const pending = getPendingBySale(sale);
    const badges = [];

    if (deposit > 0) {
      badges.push({
        key: 'deposit',
        label: 'Seña',
        value: deposit,
        color: theme.palette.warning.main,
      });
    }

    if (isReserved) {
      badges.push({
        key: 'pending',
        label: 'Debe',
        value: pending,
        color: pending > 0 ? theme.palette.error.main : theme.palette.success.main,
      });
    }

    if (!badges.length) {
      return null;
    }

    return (
      <Stack direction='row' spacing={0.55} mt={0.3} flexWrap='wrap' useFlexGap>
        {badges.map((badge) => (
          <Box
            key={badge.key}
            sx={{
              px: 0.55,
              py: 0.05,
              borderRadius: 0.7,
              border: '1px solid',
              borderColor: alpha(badge.color, 0.3),
              bgcolor: alpha(badge.color, 0.1),
            }}
          >
            <Typography variant='caption' sx={{ lineHeight: 1.2, color: 'text.secondary', whiteSpace: 'nowrap' }}>
              <Box component='span' sx={{ fontWeight: 700 }}>{badge.label}:</Box> {`$${ccyFormat(badge.value)}`}
            </Typography>
          </Box>
        ))}
      </Stack>
    );
  };

  const getStatusColor = (status) => {
    const normalized = status?.toLowerCase();
    if ([STATUS_SALE.FINISHED, 'finalizada'].includes(normalized)) return 'success';
    if ([STATUS_SALE.RESERVED, 'reservada'].includes(normalized)) return 'warning';
    if ([STATUS_SALE.CANCELED, 'cancelada'].includes(normalized)) return 'error';
    if ([STATUS_SALE.RETURNED, 'devuelta'].includes(normalized)) return 'info';
    return 'default';
  };

  const withDate = salesData.map((sale) => ({
    ...sale,
    created_date: sale?.created_at ? sale.created_at.substring(0, 10) : 'Sin fecha',
  }));

  const grouped = groupByDate
    ? withDate.reduce((acc, sale) => {
      const key = sale.created_date;
      if (!acc[key]) acc[key] = [];
      acc[key].push(sale);
      return acc;
    }, {})
    : { Todas: withDate };

  const groups = Object.entries(grouped).sort((a, b) => b[0].localeCompare(a[0]));

  const renderStatusChip = (sale) => (
    <Chip
      size='small'
      color={getStatusColor(sale?.status)}
      label={sale?.status || 'Sin estado'}
      sx={{ height: 22 }}
    />
  );

  const renderPaymentInfo = (sale) => {
    const cash = getPaymentAmount(sale, 'efectivo');
    const transfer = getPaymentAmount(sale, 'transferencia');
    return isDesktop
      ? `E: $${ccyFormat(cash)} · T: $${ccyFormat(transfer)}`
      : `E: $${ccyFormat(cash)} | T: $${ccyFormat(transfer)}`;
  };

  const getRowPalette = (sale) => {
    const main = getPaymentColor(sale);
    return {
      main,
      bg: alpha(main, 0.08),
      border: alpha(main, 0.45),
    };
  };

  if (!salesData?.length) {
    return (
      <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant='body2' color='text.secondary'>
          No hay ventas para los filtros seleccionados.
        </Typography>
      </Paper>
    );
  }

  return (
    <Stack spacing={1}>
      {groups.map(([date, sales]) => (
        <Paper key={date} elevation={0} sx={{ borderRadius: 1.5, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
          {groupByDate && (
            <Box px={1.2} py={0.7} sx={{ bgcolor: 'grey.100', borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant='subtitle2' fontWeight={700} lineHeight={1.2}>{date}</Typography>
            </Box>
          )}

          {isDesktop && (
            <Box px={1.2} py={0.6} sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
              <Box
                display='grid'
                gridTemplateColumns='minmax(260px, 1.4fr) minmax(170px, 0.9fr) 120px 140px'
                gap={1}
                alignItems='center'
              >
                <Typography variant='caption' fontWeight={700} color='text.secondary'>Artículo</Typography>
                <Typography variant='caption' fontWeight={700} color='text.secondary'>Pagos</Typography>
                <Typography variant='caption' fontWeight={700} color='text.secondary'>Estado</Typography>
                <Typography variant='caption' fontWeight={700} color='text.secondary' textAlign='right'>Total</Typography>
              </Box>
            </Box>
          )}

          <Stack spacing={0.75} p={1}>
            {sales.map((sale) => {
              const saleTotal = Number(sale?.prices_sold?.sale_price || 0) * Number(sale?.quantity_sold || 0);
              const palette = getRowPalette(sale);

              const rowKey = sale?._id || `${sale?.article?._id}-${sale?.created_at}`;

              if (isDesktop) {
                return (
                  <Box
                    key={rowKey}
                    sx={{
                      border: '1px solid',
                      borderColor: palette.border,
                      backgroundColor: palette.bg,
                      borderRadius: 1,
                      px: 0.95,
                      py: 0.75,
                    }}
                  >
                    <Box
                      display='grid'
                      gridTemplateColumns='minmax(260px, 1.4fr) minmax(170px, 0.9fr) 120px 140px'
                      gap={1}
                      alignItems='center'
                    >
                      <Box display='flex' alignItems='center' gap={0.8} minWidth={0}>
                        <Box sx={{ width: 4, height: 36, borderRadius: 1, bgcolor: palette.main, flexShrink: 0 }} />
                        <Box minWidth={0}>
                          <Typography variant='body2' fontWeight={600} noWrap>
                            {`${sale?.quantity_sold || 0} - ${sale?.article?.name || 'Sin artículo'}`}
                          </Typography>
                          {sale?.nameSale && (
                            <Typography variant='caption' color='text.secondary' noWrap sx={{ fontStyle: 'italic' }}>
                              {sale.nameSale}
                            </Typography>
                          )}
                          {renderAmountsMeta(sale)}
                          {viewDetail && (
                            <Box mt={0.25}>
                              <DiscountChip discountApplied={sale?.discount_applied} size='small' showOfferName />
                            </Box>
                          )}
                        </Box>
                      </Box>

                      <Typography variant='caption' color='text.secondary'>{`${getPaymentLabel(sale)} · ${renderPaymentInfo(sale)}`}</Typography>
                      {renderStatusChip(sale)}
                      <Typography variant='body2' fontWeight={700} textAlign='right'>{`$ ${ccyFormat(saleTotal)}`}</Typography>
                    </Box>
                  </Box>
                );
              }

              return (
                <Paper
                  key={rowKey}
                  elevation={0}
                  sx={{
                    border: '1px solid',
                    borderColor: palette.border,
                    backgroundColor: palette.bg,
                    borderRadius: 1.2,
                    p: 0.9,
                  }}
                >
                  <Box display='flex' alignItems='flex-start' justifyContent='space-between' gap={0.8}>
                    <Box display='flex' gap={0.7} minWidth={0}>
                      <Box sx={{ width: 4, borderRadius: 1, bgcolor: palette.main, alignSelf: 'stretch' }} />
                      <Box minWidth={0}>
                        <Typography variant='body2' fontWeight={600} lineHeight={1.25}>
                          {`${sale?.quantity_sold || 0} - ${sale?.article?.name || 'Sin artículo'}`}
                        </Typography>
                        {sale?.nameSale && (
                          <Typography variant='caption' color='text.secondary' sx={{ fontStyle: 'italic', lineHeight: 1.2 }}>
                            {sale.nameSale}
                          </Typography>
                        )}
                        {renderAmountsMeta(sale)}
                        <Box mt={0.35}>{renderStatusChip(sale)}</Box>
                      </Box>
                    </Box>

                    <Typography variant='body2' fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>{`$ ${ccyFormat(saleTotal)}`}</Typography>
                  </Box>

                  <Typography variant='caption' color='text.secondary' display='block' mt={0.5}>
                    {`${getPaymentLabel(sale)} · ${renderPaymentInfo(sale)}`}
                  </Typography>
                  {viewDetail && (
                    <Box mt={0.3}>
                      <DiscountChip discountApplied={sale?.discount_applied} size='small' showOfferName />
                    </Box>
                  )}
                </Paper>
              );
            })}
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
};

export default SalesAnalysisList;
